// @ts-nocheck
/* eslint-disable indent */
import PropTypes from "prop-types";
import {useCallback, useContext, useState} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {BaseButton} from "@/components";
import useGetRefParams from "@/hooks/useGetRefParams";
import useIsMobile from "@/hooks/useIsMobile";

import CustomNavbar from "../components/CustomNavbar";
import AppContext from "../providers/AppContext";
import {useAuthStore} from "../store/useAuth.store";
import colors from "../styles/colors";
import {useBrandTitle, useLogo} from "../util/logoUtils";
import {MenuVertical} from "./components/BaseMenu/arrayMenu";
import ButtonsNavbar from "./components/ButtonsNavbar";
import FooterRyg from "./components/FooterRyg";
import FooterRygMobile from "./components/FooterRygMobile";
import Wallet from "./components/Wallet";

const mainLayoutCls = "min-h-screen relative p-0 max-[1199px]:m-0 max-[640px]:pb-[93px]";
const profilePartCls =
  "flex items-center justify-end max-[1199px]:justify-between max-[1199px]:w-full max-[1199px]:[&_img]:ml-5";
const headerAuthButtonsCls =
  "max-[1368px]:[&_.btn-tertiary-hover]:w-[105px] max-[1368px]:[&_.btn-tertiary-hover]:min-w-[var(--size-20)] max-[1368px]:[&_.btn-tertiary-hover]:text-sm max-[1368px]:[&_.btn-tertiary-hover]:px-2 max-[1368px]:[&_.btn-tertiary-hover]:h-10 max-[1368px]:[&_.btn-secondary-hover]:w-[105px] max-[1368px]:[&_.btn-secondary-hover]:min-w-[var(--size-20)] max-[1368px]:[&_.btn-secondary-hover]:text-sm max-[1368px]:[&_.btn-secondary-hover]:px-2 max-[1368px]:[&_.btn-secondary-hover]:h-10";
const contentAreaCls = "m-0 p-0 relative";

const MenuIconSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3 6h18M3 12h18M3 18h18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const LOut = ({children}) => {
  const iframeRef = useContext(AppContext);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false); //responsive sidebar logo
  const logo = useLogo(); // Dynamic logo based on hostname
  const brandTitle = useBrandTitle(); // Dynamic brand title based on hostname

  useGetRefParams();

  const menuVertical = MenuVertical();
  const {t} = useTranslation();

  const widthIsMajor1200 = !useIsMobile({breakpoint: 1200});
  const widthIsMajor800 = !useIsMobile({breakpoint: 800});
  const widthIsMajor640 = !useIsMobile({breakpoint: 640});
  const widthIsMajorXL = !useIsMobile({breakpoint: 1200});

  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const authStatus = useAuthStore(state => state.authStatus);

  const selected = location.pathname.replace(/\/dashboard\/?/, "") || "index";
  const open = selected.split("/")[0];

  const onSelect = useCallback(
    ({key}) => {
      if (authStatus !== "authenticated" && key !== "/" && key !== "/support") {
        useAuthStore.getState().handleAuthForm("login");
        handleShowAuthModal();
        return;
      }

      if (key !== "/sports" && iframeRef.current?.contentWindow) {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            "killBetby",
            "https://betby-beta.raspaygana.com.ve" // Replace with the actual iframe origin
          );
        }
        // Delay navigation for 2 seconds
        const timeoutId = setTimeout(() => {
          navigate(key);
        }, 2000);
        // Clear the timeout if the user clicks another menu item before the delay
        return () => clearTimeout(timeoutId);
      }
      navigate(key);
    },
    [navigate, authStatus]
  );

  const screens = {xl: widthIsMajorXL};

  const styles = {
    header: {
      background: "linear-gradient(180deg, rgba(20,20,28,0.88) 0%, rgba(15,15,20,0.78) 100%)",
      backdropFilter: "saturate(160%) blur(18px)",
      WebkitBackdropFilter: "saturate(160%) blur(18px)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      position: "sticky",
      top: 0,
      zIndex: 50,
      height: screens.xl ? "5.25rem" : "4rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 10px 30px -18px rgba(0,0,0,0.65), inset 0 -1px 0 rgba(255,255,255,0.04)",
    },
    headerAccent: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(250,84,0,0.45) 25%, rgba(215,50,168,0.55) 50%, rgba(253,238,51,0.45) 75%, transparent 100%)",
      opacity: 0.7,
      pointerEvents: "none",
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "15%",
      width: "380px",
      height: "380px",
      background: "radial-gradient(closest-side, rgba(215,50,168,0.18) 0%, rgba(215,50,168,0) 70%)",
      filter: "blur(8px)",
      pointerEvents: "none",
    },
  };

  return (
    <div
      className={mainLayoutCls}
      style={{
        display: "flex",
        flexDirection: "row",
        maxWidth: "100vw",
        overflowX: "clip",
        margin: "0",
        padding: "0",
      }}
    >
      {/* Desktop Sidebar */}
      {widthIsMajor1200 && (
        <CustomNavbar
          items={menuVertical}
          onSelect={onSelect}
          collapsed={collapsed}
          onCollapse={() => setCollapsed(!collapsed)}
          mode="vertical"
          logo={logo}
          onLogoClick={() => navigate("/")}
          style={{
            boxShadow: "0 88px 8px rgba(0, 0, 0, 0.4)",
            overflowY: "auto",
          }}
        />
      )}

      <div style={{display: "flex", flexDirection: "column", minHeight: "100vh", minWidth: 0, flex: 1}}>
        {/* Mobile/Tablet Header - Show for unauthenticated users OR authenticated users in tablet/mobile range */}
        {!widthIsMajor1200 && (authStatus !== "authenticated" || widthIsMajor800) && (
          <div style={styles.header}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "0 20px",
              }}
            >
              {/* Hamburger Menu */}
              {widthIsMajor800 && !widthIsMajor1200 && (
                <CustomNavbar
                  items={menuVertical}
                  onSelect={onSelect}
                  mode="horizontal"
                  style={{background: "transparent"}}
                  defaultOpenKeys={[open]}
                  overflowedIndicator={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "var(--size-10)",
                        height: "var(--size-10)",
                        color: "white",
                        fontSize: "var(--font-h1)",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <MenuIconSvg />
                    </span>
                  }
                />
              )}
              {/* Mobile Logo */}
              <img
                src={logo}
                alt={brandTitle}
                style={{
                  width: "var(--size-12-5)",
                  cursor: "pointer",
                  transition: "all 0.3s ease-in-out",
                }}
                onClick={() => navigate("/")}
              />

              {/* User Actions */}
              <div style={{marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "var(--space-2-5)"}}>
                {authStatus !== "authenticated" ? (
                  <>
                    <BaseButton
                      variant="outline"
                      onClick={() => {
                        useAuthStore.getState().handleAuthForm("login");
                        handleShowAuthModal();
                      }}
                    >
                      {t("homeDisplayLoginBtn")}
                    </BaseButton>
                    <BaseButton
                      variant="primary"
                      onClick={() => {
                        useAuthStore.getState().handleAuthForm("registration");
                        handleShowAuthModal();
                      }}
                    >
                      {t("homeDisplayRegisterBtn")}
                    </BaseButton>
                  </>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-3)",
                    }}
                  >
                    <Wallet />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Desktop Header */}
        {widthIsMajor1200 && (
          <div style={styles.header}>
            <div
              style={styles.headerGlow}
              aria-hidden="true"
            />
            <div
              style={styles.headerAccent}
              aria-hidden="true"
            />
            <ErrorBoundary fallback={<h2>Something went wrong with the Match Result</h2>} />
            <div
              className={profilePartCls}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                position: "relative",
                zIndex: 1,
              }}
            >
              {!widthIsMajor1200 && (
                <img
                  src={logo}
                  alt={brandTitle}
                  style={{
                    width: "var(--size-12-5)",
                    margin: "0 auto",
                    cursor: "pointer",
                    transition: "all 0.3s ease-in-out",
                  }}
                  onClick={() => navigate("/")}
                />
              )}
              <ButtonsNavbar sidebarCollapsed={collapsed} />
              <div style={{flex: 1}} />
              {authStatus !== "authenticated" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-3)",
                    marginRight: "var(--space-6)",
                  }}
                  className={headerAuthButtonsCls}
                >
                  <BaseButton
                    onClick={() => {
                      useAuthStore.getState().handleAuthForm("login");
                      handleShowAuthModal();
                    }}
                    variant="outline"
                    size="md"
                  >
                    {t("homeDisplayLoginBtn")}
                  </BaseButton>
                  <BaseButton
                    onClick={() => {
                      useAuthStore.getState().handleAuthForm("registration");
                      handleShowAuthModal();
                    }}
                    variant="primary"
                    size="md"
                  >
                    {t("homeDisplayRegisterBtn")}
                  </BaseButton>
                </div>
              )}
              {authStatus === "authenticated" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-5)",
                    marginRight: "var(--space-3)",
                  }}
                >
                  <Wallet />
                </div>
              )}
            </div>
          </div>
        )}

        <main
          className={contentAreaCls}
          style={{
            padding: "0",
            margin: "0",
            minHeight: "calc(100vh - 64px)",
            paddingTop: !widthIsMajor1200 && authStatus === "authenticated" && !widthIsMajor800 ? "0" : "inherit",
            paddingBottom: !widthIsMajor800 ? "90px" : "0", // Add padding for fixed bottom nav on mobile
          }}
        >
          {children}
        </main>
        <footer
          style={{
            textAlign: "center",
            color: "white",
            background: colors.FOOTER,
            padding: "24px 50px",
          }}
        >
          {!widthIsMajor640 ? <FooterRygMobile /> : <FooterRyg />}
        </footer>
      </div>
    </div>
  );
};

LOut.propTypes = {
  children: PropTypes.node,
};

export default LOut;
